import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type UserPalette, useDeletePalette, usePaletteLibrary } from '@/entities/palette';
import { useErrorMessage } from '@/shared/i18n';
import {
  Button,
  ConfirmDialog,
  ErrorState,
  HelpRow,
  Icons,
  LoadingState,
  useToast,
} from '@/shared/ui';

import styles from './ColorPalettes.module.css';
import { type EditorSeed, PaletteEditor } from './PaletteEditor';
import { PaletteRow } from './PaletteRow';

/**
 * The "Palettes" section of a team workspace: its shared palettes with their creators, for every
 * member to see and use; the create / edit / delete controls appear only where the server's
 * rights (`can_create`, `can_edit`, `can_delete` of the library) allow them.
 */
export function WorkspacePalettes({
  workspaceId,
  isTeam,
}: {
  workspaceId: string;
  isTeam: boolean;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const message = useErrorMessage();
  const library = usePaletteLibrary();
  const remove = useDeletePalette();
  const [editor, setEditor] = useState<EditorSeed | null>(null);
  const [deleting, setDeleting] = useState<UserPalette | null>(null);

  if (!isTeam) return <p className={styles.muted}>{t('palettes.personalWorkspace')}</p>;
  if (library.isPending) return <LoadingState />;
  if (library.isError)
    return <ErrorState error={library.error} onRetry={() => void library.refetch()} />;
  const data = library.data;
  const palettes = data.workspace.filter((p) => p.workspace_id === workspaceId);
  const canCreate =
    data.workspaces.find((w) => w.workspace_id === workspaceId)?.can_create ?? false;
  const favorites = new Set(data.favorite_palettes);
  const anyEditable = palettes.some((p) => p.can_edit);

  return (
    <div className={styles.workspacePage} data-testid="workspace-palettes">
      <HelpRow topic={t('palettes.open')} topicId="workspace-palettes">
        <p className={styles.muted}>{t('palettes.workspaceTabHint')}</p>
      </HelpRow>
      {canCreate && (
        <div>
          <Button
            variant="primary"
            onClick={() => {
              setEditor({ name: '', colors: [], workspaceId });
            }}
          >
            <Icons.plus /> {t('palettes.create')}
          </Button>
        </div>
      )}
      {!canCreate && !anyEditable && palettes.length > 0 && (
        <p className={styles.muted}>{t('palettes.readOnly')}</p>
      )}
      {palettes.length === 0 && <p className={styles.muted}>{t('palettes.noShared')}</p>}
      <ul className={styles.list}>
        {palettes.map((palette) => (
          <PaletteRow
            key={palette.id}
            palette={palette}
            favorite={favorites.has(palette.id)}
            actions={{
              onEdit: (value) => {
                setEditor({ palette: value, name: value.name, colors: value.colors });
              },
              onDuplicate: (value) => {
                setEditor({
                  name: t('palettes.copyName', {
                    name: value.scope === 'system' ? value.id : value.name,
                  }),
                  colors: value.colors,
                  workspaceId: canCreate ? workspaceId : null,
                });
              },
              onDelete: setDeleting,
            }}
          />
        ))}
      </ul>
      {editor && (
        <PaletteEditor
          seed={editor}
          workspaces={data.workspaces}
          onClose={() => {
            setEditor(null);
          }}
        />
      )}
      {deleting && (
        <ConfirmDialog
          title={t('palettes.deleteTitle')}
          message={t('palettes.deleteMessage', { name: deleting.name })}
          confirmLabel={t('palettes.delete')}
          pending={remove.isPending}
          onCancel={() => {
            setDeleting(null);
          }}
          onConfirm={() => {
            remove.mutate(deleting.id, {
              onSettled: () => {
                setDeleting(null);
              },
              onError: (error) => {
                toast.show({ message: message(error) });
              },
            });
          }}
        />
      )}
    </div>
  );
}
